<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import {Link} from "@inertiajs/inertia-vue3";
import {capitalize, ref, watch} from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import '@vuepic/vue-datepicker/dist/main.css'
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import {Inertia} from "@inertiajs/inertia";

const props = defineProps({
  events: Object,
  users: Array,
  filters: Object,
  summaryEvents: Array,
});

const defaultFilters = {
  query: null,
  user_id: null,
  start_date: null,
  end_date: null,
  type: null
};
const filterValues = ref(Object.assign({}, defaultFilters, props.filters));
watch(() => filterValues.value.start_date, () => makeSearchRequest());
watch(() => filterValues.value.end_date, () => makeSearchRequest());
const searching = ref(false);

const statusText = (event) => {
  let status = capitalize(event.status);
  if (event.approved_at) {
    const date = dayjs(event.approved_at).format('MMMM D, YYYY hh:mm A');
    status += `<br>${date}`;
  } else if (event.declined_at) {
    const date = dayjs(event.declined_at).format('MMMM D, YYYY hh:mm A');
    status += `<br>${date}`;
  }

  return status;
}

const typeText = (event) => {
  if (event.type === 'pto') {
    return 'PTO';
  }

  return capitalize(event.type);
}

const durationText = (event) => {
  const startDate = dayjs(event.start_date);
  const endDate = dayjs(event.end_date);

  const duration = endDate.diff(startDate, 'day') + 1;
  let response = `${duration} day(s)<br>${startDate.format('MMMM DD, YYYY')}`
  if (duration > 1) {
    response += ` - ${endDate.format('MMMM DD, YYYY')}`;
  }

  return response;
}

const createdAt = (event) => {
  return dayjs(event.created_at).format('MMMM D, YYYY hh:mm A');
}

const queryParameterString = ref('');
const makeSearchRequest = _.debounce(() => executeSearchRequest(), 300);
const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['filters'] = filterValues.value;

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish() {
      searching.value = false;
    }
  });
}
</script>

<template>
  <AppLayout title="Office Calendar Report">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Office Calendar Report
      </h2>
    </template>
    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="overflow-x-auto">
          <p class="mb-2 text-gray-800">Calendar events summary. On this breakdown only User, Start Date and End Date filters apply</p>
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
            <tr>
              <th scope="col" class="px-4 py-2">User</th>
              <th scope="col" class="px-4 py-2">PTO Approved</th>
              <th scope="col" class="px-4 py-2">PTO Declined</th>
              <th scope="col" class="px-4 py-2">PTO Approval Needed</th>
              <th scope="col" class="px-4 py-2">PTO</th>
              <th scope="col" class="px-4 py-2">Business</th>
              <th scope="col" class="px-4 py-2">Other</th>
              <th scope="col" class="px-4 py-2">Total</th>
            </tr>
            </thead>
            <tbody>
            <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
                v-for="summaryEvent in summaryEvents" v-if="summaryEvents.length > 0">
              <td class="px-4 py-2 break-words" v-text="summaryEvent.user.full_name"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.pto_approved"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.pto_declined"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.pto_pending"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.pto"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.business"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.other"></td>
              <td class="px-4 py-2 break-words" v-text="summaryEvent.total"></td>
            </tr>
            <tr v-else>
              <td colspan="8" class="px-4 py-2 break-words">No records matching the criteria</td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-10 mb-2 grid gap-2 xl:grid-cols-5 md:grid-cols-2 grid-cols-1">
          <div>
            <InputLabel for="search" value="Search" class="mr-2"/>
            <TextInput
              id="search"
              v-model="filterValues.query"
              type="text"
              class="mt-1 w-full"
              placeholder="John Doe"
              @keyup="makeSearchRequest"
              :disabled="searching"
            />
          </div>
          <div>
            <InputLabel for="user" value="User"/>
            <SelectInput id="user"
                    class="mt-1"
                    v-model="filterValues.user_id"
                    @change="makeSearchRequest"
                    :disabled="searching"
            >
              <option :value="null">Please select</option>
              <option v-for="user in users" :value="user.employeeID"
                      v-text="user.full_name"></option>
            </SelectInput>
          </div>
          <div>
            <InputLabel for="start-date" value="Start Date"/>
            <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="filterValues.start_date"
                        format="MMMM d, yyyy"
                        id="start-date" model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true"
                        :disabled="searching" :enable-time-picker="false"/>
          </div>
          <div>
            <InputLabel for="end-date" value="End Date"/>
            <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="filterValues.end_date"
                        format="MMMM d, yyyy"
                        id="start-date" model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true"
                        :disabled="searching" :enable-time-picker="false"/>
          </div>
          <div>
            <InputLabel for="type" value="Type"/>
            <SelectInput id="type"
                    class="mt-1"
                    v-model="filterValues.type"
                    @change="makeSearchRequest"
                    :disabled="searching"
            >
              <option :value="null">Please select</option>
              <option value="pto">PTO</option>
              <option value="business">Business</option>
              <option value="other">Other</option>
            </SelectInput>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
            <tr>
              <th scope="col" class="px-4 py-2">User</th>
              <th scope="col" class="px-4 py-2">Title</th>
              <th scope="col" class="px-4 py-2">Description</th>
              <th scope="col" class="px-4 py-2">Duration</th>
              <th scope="col" class="px-4 py-2">Status</th>
              <th scope="col" class="px-4 py-2">Type</th>
              <th scope="col" class="px-4 py-2">Created At</th>
              <th scope="col" class="px-4 py-2">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200" v-for="event in events.data">
              <td class="px-4 py-2 break-words" v-text="event.user.full_name"></td>
              <td class="px-4 py-2 break-words" v-text="event.title"></td>
              <td class="px-4 py-2 break-words" v-html="event.description"></td>
              <td class="px-4 py-2 break-words" v-html="durationText(event)"></td>
              <td class="px-4 py-2 break-words" v-html="statusText(event)"></td>
              <td class="px-4 py-2 break-words" v-text="typeText(event)"></td>
              <td class="px-4 py-2 break-words" v-text="createdAt(event)"></td>
              <td class="px-4 py-2 break-words">
                <Link
                  :href="`${route('admin.office-calendar.show', {calendar_event: event.id})}?${queryParameterString}`">
                  <i class="fa fa-eye" :title="`View event: ${event.title}`"></i>
                </Link>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="text-center mt-5">
            <PaginationLinks :pagination="events"/>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
